// Scan a VIN with the phone camera: the barcode on the door jamb / trailer plate (Code 39, Code 128, Data Matrix or QR)
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../icons';

const VIN_IN = /[A-HJ-NPR-Z0-9]{17}/;

/** Pulls a VIN out of what a barcode says ("I1XKYD49X…" on many door labels, or a longer text) */
export function vinFromScan(text: string): string | null {
  let s = text.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (s.length === 18 && s.startsWith('I')) s = s.slice(1);   // Code 39 VIN labels often start with an "I" import marker
  const m = s.match(VIN_IN);
  return m ? m[0] : null;
}

export const canScan = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;

export function ScanVinButton({ onScan }: { onScan: (vin: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!canScan()) return null;
  return (
    <>
      <button type="button" className="btn scan-btn" onClick={e => { e.preventDefault(); setOpen(true); }} aria-label={t('scan.vin')} title={t('scan.vin')}>
        <Icon name="scan" size={20} /><span>{t('scan.short')}</span>
      </button>
      {open && createPortal(<Scanner onClose={() => setOpen(false)} onScan={v => { onScan(v); setOpen(false); }} />, document.body)}
    </>
  );
}

function Scanner({ onClose, onScan }: { onClose: () => void; onScan: (vin: string) => void }) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [seen, setSeen] = useState<string | null>(null);   // a code was read but it is not a VIN

  useEffect(() => {
    let stop: (() => void) | undefined;
    let done = false;
    (async () => {
      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([import('@zxing/browser'), import('@zxing/library')]);
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_39, BarcodeFormat.CODE_128, BarcodeFormat.DATA_MATRIX, BarcodeFormat.QR_CODE]);
        hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 120 });
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
        if (done || !video.current) { stream.getTracks().forEach(tr => tr.stop()); return; }
        const controls = await reader.decodeFromStream(stream, video.current,
          result => {
            if (!result || done) return;
            const vin = vinFromScan(result.getText());
            if (vin) { done = true; navigator.vibrate?.(60); stop?.(); onScan(vin); } else setSeen(result.getText().slice(0, 40));
          });
        stop = () => { controls.stop(); stream.getTracks().forEach(tr => tr.stop()); };
        if (done) stop();
      } catch (e) {
        const name = (e as { name?: string })?.name;
        setError(name === 'NotAllowedError' ? t('scan.denied') : name === 'NotFoundError' ? t('scan.no_camera') : t('scan.failed'));
      }
    })();
    return () => { done = true; stop?.(); };
  }, [onScan, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="scanner" role="dialog" aria-modal="true" aria-label={t('scan.vin')}>
      <video ref={video} playsInline muted autoPlay />
      <div className="scan-frame" aria-hidden="true"><i /></div>
      <div className="scan-top">
        <b>{t('scan.vin')}</b>
        <button type="button" className="scan-close" onClick={onClose} aria-label={t('common.close')}>×</button>
      </div>
      <div className="scan-help">
        {error ?? (seen ? t('scan.not_vin', { code: seen }) : t('scan.help'))}
      </div>
    </div>
  );
}
